import { useInfiniteQuery } from "@tanstack/react-query";
import { Select, Spin } from "antd";
import { type ApiCidade, type ApiCidadeGetParams, apiCidadeGet } from "features/cidade-uf-pais/api";
import { useEffect, useMemo, useState } from "react";
import { DEBOUNCE_TIME } from "std/const/debounceTime";
import { useDebouncedValue } from "std/hooks/useDebouncedValue";
import type { ScrollEvent } from "std/types";
import { filterOption } from "std/util";

type OptionCidade = {
	value: number | string;
	label: string;
	cidade: ApiCidade;
};

export function SelectCidade({
	uf,
	disabled,
	value,
	onChange,
}: {
	uf?: string;
	disabled?: boolean;
	value?: number | string;
	onChange?: (value: number | string | null, cidade: ApiCidade | null) => void;
}) {
	const [enabled, setEnabled] = useState(false);
	const [search, setSearch] = useState("");
	const [searchDebounce] = useDebouncedValue(search, DEBOUNCE_TIME);
	const [pesquisando, setPesquisando] = useState(false);
	const [options, setOptions] = useState<OptionCidade[]>([]);
	const [total, setTotal] = useState(0);

	const mode = typeof value === "string" ? "cid_cidade" : "cid_idpk";

	const baseParams: ApiCidadeGetParams = useMemo(() => {
		const innerParams: ApiCidadeGetParams = { uf, cidade: searchDebounce };

		if (!enabled) {
			if (typeof value === "number") {
				innerParams.where = `cid_idpk=${value}`;
			}

			if (typeof value === "string") {
				innerParams.cidade = value;
				innerParams.cidade_nome_exato = "S";
			}
		}

		return innerParams;
	}, [uf, searchDebounce, enabled, value]);

	const getCidades = (pageParam: number): Promise<ApiCidade[]> => {
		const params: ApiCidadeGetParams = {
			...baseParams,
			qtde_registros: 20,
			registro_inicial: pageParam * 20,
			total_registros: pageParam === 0 ? "S" : "N",
			orderby: "cid_cidade",
		};

		return apiCidadeGet({ params });
	};

	const { data, isLoading, fetchNextPage } = useInfiniteQuery({
		queryKey: ["apiCidadeGet", baseParams],
		queryFn: ({ pageParam }) => getCidades(pageParam),
		initialPageParam: 0,
		getNextPageParam: (_, __, lastPageParam) => lastPageParam + 1,
		enabled,
	});

	useEffect(() => {
		if (data && enabled) {
			setPesquisando(false);

			if (data.pages[0]?.[0]?.total_registros) {
				setTotal(data.pages[0][0].total_registros);
			}

			setOptions(
				data.pages.flat().map((cidade) => ({
					value: cidade[mode],
					label: `${cidade.cid_cidade} - ${cidade.cid_uf}`,
					cidade,
				})),
			);
		} else if (value) {
			setEnabled(true);
		}
	}, [data, enabled, value, mode]);

	useEffect(() => {
		if (typeof uf === "string") {
			setEnabled(true);
		}
	}, [uf]);

	const handleSearch = (value: string): void => {
		setEnabled(true);
		setPesquisando(true);
		setSearch(value);
		setOptions([]);
	};

	const handleSelect = (_, option: OptionCidade): void => {
		onChange?.(option.cidade[mode], option.cidade);
	};

	const onClear = (): void => {
		onChange?.(null, null);
	};

	const onPopupScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
		const { target } = e as unknown as ScrollEvent;

		if (!isLoading && target.scrollTop + target.offsetHeight === target.scrollHeight && options.length < total) {
			fetchNextPage();
		}
	};

	return (
		<Spin spinning={isLoading}>
			<Select
				allowClear={true}
				showSearch={true}
				placeholder="Selecione uma cidade"
				value={value}
				disabled={disabled}
				loading={isLoading}
				options={options}
				style={{ width: "100%" }}
				searchValue={search}
				notFoundContent={isLoading || pesquisando ? "Pesquisando..." : undefined}
				filterOption={filterOption}
				onSearch={handleSearch}
				onSelect={handleSelect}
				onClear={onClear}
				onPopupScroll={onPopupScroll}
			/>
		</Spin>
	);
}
